import { TASK_STATUSES, createDefaultCompany, createDefaultTaskDraft } from './flowdeskDataModel';

export const getFallbackCompanyKey = (companies) => Object.keys(companies || {})[0] || '';

export const calculateTaskDeadline = (taskDraft, now = new Date()) => {
  let deadline = new Date(now);

  if (taskDraft.dueDateType === 'tomorrow') {
    deadline.setDate(deadline.getDate() + 1);
    deadline.setHours(17, 0, 0, 0);
  } else if (taskDraft.dueDateType === 'custom' && taskDraft.customDate) {
    deadline = new Date(taskDraft.customDate);
    deadline.setHours(17, 0, 0, 0);
  } else {
    deadline.setHours(17, 0, 0, 0);
  }

  return deadline;
};

export const createTaskFromDraft = ({ draft, companies, now = new Date() }) => {
  const fallbackCompanyKey = getFallbackCompanyKey(companies);
  const taskCompanyKey = companies?.[draft.category] ? draft.category : fallbackCompanyKey;
  const deadline = calculateTaskDeadline(draft, now);

  return {
    id: Date.now(),
    ...draft,
    category: taskCompanyKey,
    status: draft.status || TASK_STATUSES.scheduled,
    elapsedTime: 0,
    isRunning: false,
    createdAt: now.toISOString(),
    startedAt: null,
    deadlineDate: deadline.toISOString().split('T')[0],
    deadlineTime: deadline.toTimeString().slice(0, 5),
    deadlineISO: deadline.toISOString(),
    completedAt: null
  };
};

export const addTaskToList = (tasks, task) => [task, ...(tasks || [])];

export const updateTaskInList = (tasks, updatedTask) => (
  (tasks || []).map(task => String(task.id) === String(updatedTask.id) ? updatedTask : task)
);

export const updateTaskFieldInList = ({ tasks, taskId, field, value }) => {
  const cleanValue = typeof value === 'string' ? value.trim() : value;

  return (tasks || []).map((task) => {
    if (String(task.id) !== String(taskId)) return task;

    return {
      ...task,
      [field]: cleanValue,
      ...(field === 'brief' ? { hasBrief: Boolean(cleanValue) } : {}),
      ...(field === 'tov' ? { hasTov: Boolean(cleanValue) } : {})
    };
  });
};

const getStartTimingUpdates = (task, now = new Date()) => {
  if (task.startedAt) return {};

  const deadline = new Date(now.getTime() + Number(task.estimatedHours || 1) * 60 * 60 * 1000);

  return {
    startedAt: now.toISOString(),
    deadlineISO: deadline.toISOString(),
    deadlineDate: deadline.toISOString().split('T')[0],
    deadlineTime: deadline.toTimeString().slice(0, 5)
  };
};

export const moveTaskInList = ({ tasks, taskId, status, now = new Date() }) => (
  (tasks || []).map((task) => {
    if (task.id !== taskId) return task;

    let updates = { status };
    if ([TASK_STATUSES.review, TASK_STATUSES.done, TASK_STATUSES.scheduled].includes(status)) updates.isRunning = false;
    if ([TASK_STATUSES.inProgress, TASK_STATUSES.revision].includes(status)) updates.isRunning = true;
    if (status === TASK_STATUSES.done) updates.completedAt = now.toISOString();

    if ([TASK_STATUSES.inProgress, TASK_STATUSES.revision].includes(status)) {
      updates = { ...updates, ...getStartTimingUpdates(task, now) };
    }

    return { ...task, ...updates };
  })
);

export const toggleTaskTimerInList = ({ tasks, taskId, now = new Date() }) => (
  (tasks || []).map((task) => {
    if (task.id !== taskId) return task;

    const isStarting = !task.isRunning;
    let updates = {
      isRunning: isStarting,
      status: isStarting ? TASK_STATUSES.inProgress : task.status
    };

    if (isStarting) {
      updates = { ...updates, ...getStartTimingUpdates(task, now) };
    }

    return { ...task, ...updates };
  })
);

export const deleteTaskFromList = (tasks, taskId) => (
  (tasks || []).filter(task => task.id !== taskId)
);

export const createCompany = ({ name, theme, lang = 'en', now = Date.now() }) => {
  const id = `company_${now}`;
  const fallbackName = lang === 'ar' ? '\u0634\u0631\u0643\u0629 \u062c\u062f\u064a\u062f\u0629' : 'New Company';

  return createDefaultCompany({
    id,
    name: String(name || '').trim() || fallbackName,
    theme,
    lang
  });
};

export const addCompanyToMap = (companies, company) => ({
  ...(companies || {}),
  [company.id]: company
});

export const addNamedItemToCompany = ({ companies, companyId, field, value }) => {
  const cleanValue = String(value || '').trim();
  if (!cleanValue || !companies?.[companyId]) return companies;

  return {
    ...companies,
    [companyId]: {
      ...companies[companyId],
      [field]: [...(companies[companyId][field] || []), cleanValue]
    }
  };
};

export const removeNamedItemFromCompany = ({ companies, companyId, field, index }) => {
  if (!companies?.[companyId]) return companies;

  return {
    ...companies,
    [companyId]: {
      ...companies[companyId],
      [field]: (companies[companyId][field] || []).filter((_, itemIndex) => itemIndex !== index)
    }
  };
};

export const resetTaskDraftForCompany = (companyId) => createDefaultTaskDraft(companyId);
